var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { Avatar, Divider, Tooltip } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import expandIcon from 'assets/expand.svg';
import collapseIcon from 'assets/collapse.svg';
import chevronIcon from 'assets/chevron.svg';
import signOutIcon from 'assets/sign_out.svg';
import { signOut } from 'apollo/auth';
import { getFromStorage, setToStorage } from 'utils/localStorage';
import { PermissionsContext, ACTIONS } from 'context/Permissions';
import NotificationsContext from 'context/Notifications/NotificationsContext';
import { getCompactTagVersion } from 'utils/renderHelpers';
import useUserStore from 'stores/user';
import CreateTicketButton from 'components/Header/components/CreateTicketButton/CreateTicketButton';
import LeftNavLink from 'components/LeftNav/LeftNavLink';
import { MY_WORK_LINKS, DIRECTORY_LINKS, MEMBER_TOOLS_LINKS, RESOURCES_LINKS, METABASE_ITEM, OPERATIONS_LINKS, ADMIN_LINKS, NavIcon } from 'components/LeftNav/constants';
import { StyledMenu, MenuWrap, SignUpWrapper, LogoWrapper, LogoButton, CollapsedLogoIcon, StyledLogo, CreateTicketWrapper, SignoutButton, UserProfileCard, UserProfileInfo, UserProfileName, NavScrollWrapper, NavScrollArea, CollapseButton, CollapsedAvatar, CollapsedActions, CollapsedGroup, CollapsedGroupLabel, CollapsedNavItem, NavIconWrapper, NavBadgeDot, ChevronIcon, TagVersion } from 'components/LeftNav/styled';
import { PURPLE_AVATAR, NAVY } from 'ui/constants';
import { ADVOCATE_PROFILE_MY_PROFILE_PATH, TASKS_PATH } from 'constants/routes';
import logo from 'assets/logo.svg';
import logoIcon from 'assets/logo_cut.svg';
var LOCAL_STORAGE_KEY = 'open_menu_items';
var tagVersion = getCompactTagVersion(__TAG_VERSION__);
var getInitialsFromFullName = function (name) {
    if (!name)
        return '';
    return name
        .split(' ')
        .map(function (part) { return part[0]; })
        .slice(0, 2)
        .join('')
        .toUpperCase();
};
var buildNavItem = function (_a) {
    var pathTo = _a.pathTo, dataTest = _a.dataTest, render = _a.render, title = _a.title, icon = _a.icon;
    return ({
        key: pathTo,
        icon: icon,
        title: title,
        label: (_jsx("div", { "data-test": dataTest, children: _jsx(LeftNavLink, { pathTo: pathTo, title: title, render: render }) }))
    });
};
var METABASE_URL = 'https://metabase.rightwayhealthcare.com';
var metabaseNavItem = {
    key: 'metabase',
    icon: METABASE_ITEM.icon,
    title: METABASE_ITEM.title,
    label: (_jsx("div", { "data-test": METABASE_ITEM.dataTest, children: _jsx("a", { target: "_blank", href: METABASE_URL, rel: "noopener noreferrer", children: METABASE_ITEM.title }) }))
};
var filterVisibleLinks = function (links, ability) {
    return links.filter(function (_a) {
        var permissionSubject = _a.permissionSubject;
        return permissionSubject ? ability.can(ACTIONS.READ, permissionSubject) : true;
    });
};
var NAV_GROUPS = [
    {
        key: 'my_work',
        title: 'My Work',
        dataTest: 'leftNavMyWork',
        links: MY_WORK_LINKS
    },
    {
        key: 'directory',
        title: 'Directory',
        dataTest: 'leftNavDirectory',
        links: DIRECTORY_LINKS
    },
    {
        key: 'member_tools',
        title: 'Member Tools',
        dataTest: 'leftNavMemberTools',
        links: MEMBER_TOOLS_LINKS
    },
    {
        key: 'resources',
        title: 'Resources',
        dataTest: 'leftNavResources',
        links: RESOURCES_LINKS,
        extraItems: [metabaseNavItem]
    },
    {
        key: 'operations',
        title: 'Operations',
        dataTest: 'leftNavOperations',
        links: OPERATIONS_LINKS
    },
    {
        key: 'admin',
        title: 'Admin',
        dataTest: 'leftNavAdmin',
        links: ADMIN_LINKS
    }
];
var SHORT_LABELS = {
    my_work: 'WORK',
    directory: 'DIR',
    member_tools: 'TOOLS',
    resources: 'RES',
    operations: 'OPS',
    admin: 'ADMIN'
};
var renderExpandIcon = function (_a) {
    var isOpen = _a.isOpen;
    return (_jsx(ChevronIcon, { src: chevronIcon, alt: "", "$isOpen": isOpen }));
};
var LeftNav = function (_a) {
    var _b = _a.collapsed, collapsed = _b === void 0 ? false : _b, onToggle = _a.onToggle;
    var ability = useAbility(PermissionsContext);
    var navigate = useNavigate();
    var pathname = useLocation().pathname;
    var userName = useUserStore(function (s) { return s.userName; });
    var _c = useContext(NotificationsContext), chatUnreadCount = _c.chatUnreadCount, taskUnreadCount = _c.taskUnreadCount;
    var onOpenChange = function (openKeys) {
        setToStorage(LOCAL_STORAGE_KEY)(openKeys);
    };
    var selectedKey = "/".concat(pathname.split('/')[1]);
    var id = pathname.startsWith('/members/')
        ? pathname.split('/')[2]
        : undefined;
    var defaultOpenKeys = getFromStorage(LOCAL_STORAGE_KEY)() || ['my_work', 'directory', 'member_tools', 'resources'];
    var navItems = useMemo(function () {
        return NAV_GROUPS.flatMap(function (_a) {
            var key = _a.key, title = _a.title, dataTest = _a.dataTest, links = _a.links, _b = _a.extraItems, extraItems = _b === void 0 ? [] : _b;
            var children = __spreadArray(__spreadArray([], filterVisibleLinks(links, ability).map(buildNavItem), true), extraItems, true);
            if (!children.length)
                return [];
            return [
                {
                    key: key,
                    label: _jsx("span", { "data-test": dataTest, children: title }),
                    children: children
                }
            ];
        });
    }, [ability]);
    if (collapsed) {
        return (_jsxs(MenuWrap, { children: [_jsx(LogoWrapper, { style: { justifyContent: 'center', padding: '16px 0', boxSizing: 'border-box', height: '68px' }, children: _jsx(LogoButton, { type: "button", onClick: onToggle, children: _jsx(CollapsedLogoIcon, { src: logoIcon, alt: "Rightway" }) }) }), _jsx(Divider, { style: { margin: 0 } }), _jsx(CreateTicketWrapper, { style: { padding: '8px', display: 'flex', justifyContent: 'center' }, children: _jsx(CreateTicketButton, { id: id, iconOnly: true }) }), _jsx(NavScrollWrapper, { children: _jsx(NavScrollArea, { children: navItems.map(function (group) {
                            var _a;
                            return (_jsxs(CollapsedGroup, { children: [_jsx(CollapsedGroupLabel, { children: SHORT_LABELS[group.key] }), (_a = group.children) === null || _a === void 0 ? void 0 : _a.map(function (rawItem) {
                                        var item = rawItem;
                                        if (item.key === 'metabase') {
                                            return (_jsx(Tooltip, { title: item.title, placement: "right", children: _jsx(CollapsedNavItem, { as: "a", href: METABASE_URL, target: "_blank", rel: "noopener noreferrer", children: item.icon }) }, item.key));
                                        }
                                        return (_jsx(Tooltip, { title: item.title, placement: "right", children: _jsx(CollapsedNavItem, { as: Link, to: item.key, "$selected": selectedKey === item.key, children: (item.key === '/chats' && chatUnreadCount > 0) ||
                                                    (item.key === TASKS_PATH && taskUnreadCount > 0) ? (_jsxs(NavIconWrapper, { children: [item.icon, _jsx(NavBadgeDot, {})] })) : (item.icon) }) }, item.key));
                                    })] }, group.key));
                        }) }) }), _jsx(Divider, { style: { margin: '4px 0' } }), _jsx(Tooltip, { title: "Profile", placement: "right", children: _jsx(CollapsedAvatar, { onClick: function () { return navigate(ADVOCATE_PROFILE_MY_PROFILE_PATH); }, "data-test": "leftNavUserProfile", children: _jsx(Avatar, { size: 32, style: {
                                backgroundColor: PURPLE_AVATAR,
                                color: NAVY,
                                fontSize: 13,
                                fontWeight: 600,
                                lineHeight: '19px',
                                fontFamily: 'Inter'
                            }, children: getInitialsFromFullName(userName) }) }) }), _jsxs(CollapsedActions, { children: [_jsx(Tooltip, { title: "Expand", placement: "right", children: _jsx(CollapsedNavItem, { onClick: onToggle, "data-test": "sidebarCollapseBtn", children: _jsx(NavIcon, { src: expandIcon }) }) }), _jsx(Tooltip, { title: "Sign out", placement: "right", children: _jsx(CollapsedNavItem, { onClick: function () {
                                    signOut();
                                }, "data-test": "leftNavSignOutBtn", children: _jsx(NavIcon, { src: signOutIcon }) }) })] })] }));
    }
    return (_jsxs(MenuWrap, { children: [_jsxs(LogoWrapper, { children: [_jsx(StyledLogo, { src: logo, alt: "Rightway Healthcare", "data-test": "leftNavLogo" }), tagVersion && _jsxs(TagVersion, { children: ["v", tagVersion] }), _jsx(CollapseButton, { onClick: onToggle, "data-test": "sidebarCollapseBtn", children: _jsx(NavIcon, { src: collapseIcon }) })] }), _jsx(Divider, { style: { margin: 0 } }), _jsx(CreateTicketWrapper, { children: _jsx(CreateTicketButton, { id: id }) }), _jsx(NavScrollWrapper, { children: _jsx(NavScrollArea, { children: _jsx(StyledMenu, { mode: "inline", defaultOpenKeys: defaultOpenKeys, onOpenChange: onOpenChange, selectedKeys: [selectedKey], defaultSelectedKeys: [selectedKey], items: navItems, expandIcon: renderExpandIcon }) }) }), _jsx(Divider, { style: { margin: '4px 0' } }), _jsxs(UserProfileCard, { onClick: function () { return navigate(ADVOCATE_PROFILE_MY_PROFILE_PATH); }, "data-test": "leftNavUserProfile", children: [_jsx(Avatar, { size: 36, style: {
                            backgroundColor: PURPLE_AVATAR,
                            color: NAVY,
                            fontSize: 13,
                            fontWeight: 600,
                            lineHeight: '19px',
                            fontFamily: 'Inter'
                        }, children: getInitialsFromFullName(userName) }), _jsx(UserProfileInfo, { children: _jsx(UserProfileName, { children: userName }) }), _jsx(RightOutlined, { style: { fontSize: 12, flexShrink: 0 } })] }), _jsx(Divider, { style: { margin: '4px 0' } }), _jsx(SignUpWrapper, { children: _jsx(SignoutButton, { icon: _jsx(NavIcon, { src: signOutIcon }), onClick: function () {
                        signOut();
                    }, "data-test": "leftNavSignOutBtn", children: "Sign out" }) })] }));
};
export default React.memo(LeftNav);
